import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import Image from 'components/Common/Image'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TourV2HostsSponsors from 'components/OfferPage/TourV2/TourV2HostsSponsors'
import TourV2OverviewPanel from 'components/OfferPage/TourV2/TourV2OverviewPanel'
import TourOffer2027TravelBanner from 'components/Tours/TourOffer2027TravelBanner'
import TourV2UrgencyTags from 'components/Tours/TourV2UrgencyTags'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { DMY_CASUAL_LONG_FORMAT, LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import {
  getTour2027LaunchCampaignConfig,
  getTour2027LaunchCampaignTier,
  isTour2027LaunchCampaignActive,
  TourV2ContentSections,
} from 'constants/tours'
import ModalContext from 'contexts/ModalContext'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import type { ProductPalette } from 'contexts/ProductPaletteContext'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { useAppSelector } from 'hooks/reduxHooks'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import useNavigateTo from 'hooks/useNavigateTo'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import { removeConsecutiveDuplicates } from 'lib/array/arrayUtils'
import { isCruiseFlashOfferV2 } from 'lib/offer/offerTypes'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { isTourLimitedTimeOffer, isTourProductTypeStyled } from 'lib/tours/tourUtils'
import moment from 'moment'
import { rem } from 'polished'
import { Fragment, useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getIsTourOfferPurchasable, getSelectedVariationDepartureBoundaries } from 'selectors/TourV2/tourV2OfferPageSelectors'
import styled from 'styled-components'
import TourBookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/TourBookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import TourV2DepartureBookingModal from './Departures/TourV2DepartureBookingModal'
import TourStyleKeyBenefitsSection from './TourStyleKeyBenefitsSection'
import TourV2HighlightedInclusions from './TourV2HighlightedInclusions'
import TourTermsAndConditionsModal from './TourTermsAndConditionsModal'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import useOfferPageWalledGarden from 'hooks/OfferPage/useOfferPageWalledGarden'
import { scrollToId } from 'lib/window/scrollUtils'
import type { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import { getCanAgentExportMedia } from 'agentHub/selectors/agentHubSelectors'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'

const SUPPORTED_BY_OFFER_IDS = new Set([
  'tour-8088b454-63cc-458d-8a79-1ead498e3833',
  'tour-4523e4bf-2bac-49e3-8785-b75fe9752510',
])

const SUPPRESS_SPONSOR_HEADING_OFFER_IDS = new Set([
  'tour-7e3c112c-c54c-4632-87e8-6eafa6c99bed',
])

const GridContainer = styled.div`
  display: grid;
  grid-template:
    "top-bar"
    "content"
    "overview"
    / 1fr;
  grid-row-gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    grid-template:
      "top-bar top-bar"
      "content overview"
      / 1fr minmax(min-content, ${rem(400)});
    grid-row-gap: ${rem(8)};
    grid-column-gap: ${rem(32)};
  }

  > .top-bar-area {
    grid-area: top-bar;
  }

  > .content-area {
    grid-area: content;
  }

  > .overview-area {
    grid-area: overview;
  }
`

const DescriptionArea = styled(VerticalSpacer)`
  grid-area: description;
  margin-top: ${rem(16)};
`

const ContentGridContainer = styled.div`
  display: grid;
  grid-template:
    "title" min-content
    "details" min-content
    "review" min-content
    "overview-panel" min-content
    "promo" min-content
    "description" min-content
    / 1fr;

  > .title-area {
    grid-area: title;
  }
  > .details-area {
    grid-area: details;
    margin-top: ${rem(8)};
  }
  > .review-area {
    grid-area: review;
    margin-top: ${rem(12)};
    &:empty {
      display: none;
    }
  }
  > .overview-panel-area {
    grid-area: overview-panel;
    margin-top: ${rem(16)};
    &:empty {
      display: none;
    }
  }
  > .promo-area {
    grid-area: promo;
    margin-top: ${rem(4)};
    &:empty {
      display: none;
    }
  }
`

const OperatorLogoImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
`

const OperatorLogoContainer = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
    flex-shrink: 0;
    max-height: ${rem(75)};
    max-width: ${rem(150)};
    align-self: center;
  }
`

const Description = styled(MarkdownRender)`
  line-height: ${rem(24)};
`

const OverviewTourOffer2027TravelBanner = styled(TourOffer2027TravelBanner)`
  width: 100%;
  height: fit-content;
  background-color: transparent;
  align-items: flex-start;
  justify-content: center;
  padding: ${rem(8)} 0 0;

  > div {
    width: 100%;
    max-width: none;
    height: fit-content;
    padding: ${rem(32)} ${rem(20)} ${rem(16)};
  }

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(8)};

    > div {
      padding: ${rem(36)} ${rem(24)} ${rem(18)};
    }
  }

  > div > div:first-child {
    font-size: 14px;
    line-height: 20px;
  }
`

const DetailItemsContainer = styled(Group)`
  &:empty {
    display: none;
  }

  /* Apply no-wrap styling to text elements */
  > * {
    white-space: nowrap;
  }
`

const VerticalDivider = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
    height: ${rem(24)};
    width: 1px;
    background-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

interface MappedProps {
  latestDeparture?: App.Tours.TourV2OfferDeparture
  variation: App.Tours.TourV2OfferVariation
  offer: App.Tours.TourV2Offer
  isOfferPurchasable: boolean
}

interface Props {
  cheapestPurchasableOption?: App.Tours.TourV2OfferPurchasableOption
  onView?: (ref: string) => void
  shareModalConfig: ShareConfig
  buttonPaletteValue: ProductPalette
}

function TourV2Overview(props: Props & MappedProps) {
  const {
    cheapestPurchasableOption,
    isOfferPurchasable,
    latestDeparture,
    offer,
    onView,
    shareModalConfig,
    variation,
    buttonPaletteValue,
  } = props

  const isCruiseFlashOffer = isCruiseFlashOfferV2(offer)

  // operator
  const operatorName = offer.operatedBy
  const operatorLogo = offer.brand?.logoImage

  // Activity level
  const activityLevel = offer.activityLevel

  const availableDepartures = useMemo(() => {
    if (!offer.departures) return []
    return Object.values(offer.departures)
      .filter(e => e.status === 'available' && e.fkVariationId === variation.id)
  }, [offer.departures, variation.id])

  // last available booking date
  const lastAvailableBookingDate = useMemo(() => {
    if (!latestDeparture) return
    if (latestDeparture.status !== 'available') return
    const dateFormat = availableDepartures.length === 1 ? DMY_CASUAL_LONG_FORMAT : LONG_MONTH_LONG_YEAR
    const lastDate = moment(latestDeparture.startDate).format(dateFormat)
    return lastDate
  }, [latestDeparture, availableDepartures])

  const { description } = useTourV2VariationDetails(variation)

  const showWall = useOfferPageWalledGarden()
  const isEurope = useEuropeOfferPageWalledGarden()

  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)
  const navigateTo = useNavigateTo()

  const onCheckAvailability = useCallback(() => {
    if (showWall && !isEurope) {
      scrollToId('soft-wall', -50)
    } else if (isMobile) {
      showModal(<TourV2DepartureBookingModal offer={offer} />)
    } else {
      onView?.(TourV2ContentSections.DEPARTURES)
    }
  }, [isEurope, isMobile, offer, onView, showModal, showWall])

  const onLinkedTourClick = useCallback(() => {
    const linkedTour = offer.nextYearTour || offer.previousYearTour
    if (linkedTour) {
      navigateTo(`/tour/tour-${linkedTour.tourId}`)
    }
  }, [navigateTo, offer.nextYearTour, offer.previousYearTour])

  const scrollToReviews = useCallback(() => { onView?.(TourV2ContentSections.REVIEWS) }, [onView])
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const cheapestOptionPrice = useMemo(() => {
    return findCheapestTourV2PurchasableOption(offer, variation.id)?.price ?? 0
  }, [offer, variation.id])
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined
  const canAgentExportMedia = getCanAgentExportMedia

  const intl = useIntl()
  const sponsorLabel = SUPPORTED_BY_OFFER_IDS.has(offer.id)
    ? intl.formatMessage({
        id: 'tDJskMSoAS7cAV3e2kNG',
        defaultMessage: 'Supported by',
        description: 'Sponsor label shown above tour sponsor logos on the tour offer page',
      })
    : SUPPRESS_SPONSOR_HEADING_OFFER_IDS.has(offer.id) ? '' : undefined
  const departureDateText = useMemo(() => {
    if (availableDepartures) {
      return (availableDepartures.length === 1
        ? intl.formatMessage({
            id: 'vj__a74-FaZkdVqBmtC6',
            defaultMessage: 'Tour departs:',
            description: 'Label prefix for single departure date on the tour offer detail page',
          })
        : intl.formatMessage({
            id: '-Kkb7B_sBcNuKahlQ4en',
            defaultMessage: 'Book up until',
            description: 'Label prefix for multiple departure dates in the tours section',
          }))
    }
  }, [availableDepartures, intl])

  const linkedTourText = useMemo(() => {
    const linkedTour = offer.nextYearTour || offer.previousYearTour
    return linkedTour
      ? intl.formatMessage(
          {
            id: 'WqKieP50tR8PGvY-jHyO',
            defaultMessage: 'View {year} departures',
            description: 'Hyperlink text to view departures for a specific year',
          },
          { year: linkedTour.assignYear },
        )
      : null
  }, [offer.nextYearTour, offer.previousYearTour, intl])

  const itineraryLocations = useMemo(() => {
    return removeConsecutiveDuplicates(variation.itinerary
      .flatMap(itinerary => itinerary.locationsVisitedDetails)
      .map(location => location.name))
  }, [variation])

  const isLimitedTimeOffer = isTourLimitedTimeOffer(offer)
  const tour2027LaunchCampaignConfig = getTour2027LaunchCampaignConfig()
  const campaignTier = useMemo(() => {
    if (!isTour2027LaunchCampaignActive(region, config.BRAND)) {
      return undefined
    }

    return getTour2027LaunchCampaignTier(offer.id)
  }, [offer.id, region])
  const shouldShowToolTip = useScreenSizeUp('tablet')
  const openTermsAndConditionsModal = useCallback(() => {
    showModal(<TourTermsAndConditionsModal
      termsAndConditions={campaignTier?.termsAndConditions}
    />)
  }, [showModal, campaignTier])

  return (
    <ProductPaletteProvider palette={buttonPaletteValue}>
      <Group direction="vertical" gap={20}>
        <GridContainer>
          <Group
            className="top-bar-area"
            direction="horizontal-reverse"
            horizontalAlign="space-between"
            verticalAlign="center"
            gap={8}
          >
            <CSSBreakpoint min="tablet">
              <Group direction="horizontal" verticalAlign="center" gap={8}>
                <ShareModal shareConfig={shareModalConfig} openOnHash="#share">
                  <ShareWithFriendsButton size="small" showToolTip={shouldShowToolTip} />
                </ShareModal>
                {isExportMediaEnabled && canAgentExportMedia && <ExportMediaContentButton size="small" target="_blank" href={mediaExportURL} />}
                <TripGuard>
                  <TourBookmarkButton offer={offer} selectedVariation={variation} />
                </TripGuard>
              </Group>
            </CSSBreakpoint>
            <TourV2UrgencyTags offer={offer} />

          </Group>
          <ContentGridContainer className="content-area">
            <TripGuard>
              <ImmersiveSaveButtonPortal>
                <TourBookmarkButton offer={offer} selectedVariation={variation} />
              </ImmersiveSaveButtonPortal>
            </TripGuard>
            <Group
              className="title-area"
              data-testid="tourV2-offer-overview"
              direction="horizontal"
              horizontalAlign="space-between"
              gap={8}
            >
              <Heading as="h1" variant="heading2">
                {variation.name}
              </Heading>
              {operatorLogo && <OperatorLogoContainer>
                <OperatorLogoImage
                  id={operatorLogo.id}
                  dpr={2}
                  quality="eco"
                  width={150}
                  alt={operatorLogo.title}
                  aria-hidden="true"
                />
              </OperatorLogoContainer>}
            </Group>
            <DetailItemsContainer direction="vertical" desktopDirection="horizontal" desktopWrap="wrap" desktopGap={16} gap={4} className="details-area">
              {operatorName && <BodyText variant="large">
                <span>
                  {intl.formatMessage(
                    {
                      id: 'wO2zZ9vUZaKbk1C2LzfF',
                      defaultMessage: 'Operated by: {operatorName}',
                      description: 'Label showing tour operator name on the tour offer detail page',
                    },
                    {
                      operatorName: <b key="operator">{operatorName}</b>,
                    },
                  )}
                </span>
              </BodyText>}
              <VerticalDivider />
              {!isLimitedTimeOffer && lastAvailableBookingDate &&
                <BodyText variant="large">
                  {departureDateText} &nbsp;
                  <TextLink onClick={onCheckAvailability}>
                    {lastAvailableBookingDate}
                  </TextLink>
                </BodyText>}
              {isLimitedTimeOffer && offer.schedule &&
                <BodyText variant="large">
                  {intl.formatMessage(
                    {
                      id: 'UtsZ-MUthFtReXCXZO9n',
                      defaultMessage: 'Book by {date}',
                      description: 'Form field label for limited time offer booking deadline',
                    },
                    {
                      date: <TextLink key="date" onClick={onCheckAvailability}><FormatDateTime value={offer.schedule.end} format="day-long-month-long-year" /></TextLink>,
                    },
                  )}
                </BodyText>}
              <VerticalDivider />
              {linkedTourText && <TextLink onClick={onLinkedTourClick}>{linkedTourText}</TextLink>}
            </DetailItemsContainer>
            {isOfferRatingDisplayable(offer.rating, 0) && <div className="review-area">
              <OfferRating
                link
                inlineLabel
                variant="small"
                rating={offer.rating}
                onReviewSubTitleClick={scrollToReviews}
              />
            </div>}
            <div className="overview-panel-area">
              <CSSBreakpoint max="mobile">
                <TourV2OverviewPanel
                  offer={offer}
                  activityLevel={activityLevel}
                  purchasableOption={cheapestPurchasableOption}
                  isOfferPurchasable={isOfferPurchasable}
                  productType={offer.productType}
                  variation={variation}
                  onCheckAvailability={onCheckAvailability}
                  guideType={offer.guideType}
                />
              </CSSBreakpoint>
            </div>
            <div className="promo-area">
              {isTasteOfGoldActive && isTasteOfGoldEligibleOffer(offer, currency, variation.countriesVisited.some(c => c.toLowerCase() !== 'australia'), cheapestOptionPrice) && <TasteOfGoldPromoCard>
                <CSSBreakpoint max="mobile">
                  <TasteOfGoldPromoContent layout="vertical" size="medium" context="offerPage" />
                </CSSBreakpoint>
                <CSSBreakpoint min="tablet">
                  <TasteOfGoldPromoContent layout="horizontal" size="medium" context="offerPage" />
                </CSSBreakpoint>
              </TasteOfGoldPromoCard>}
            </div>
            <DescriptionArea gap={24}>
              <VerticalSpacer gap={12}>
                {!isCruiseFlashOffer && <Heading variant="heading4">
                  {intl.formatMessage({
                    id: 'UUtw9XsfzTlmkoDqWI88',
                    defaultMessage: 'About the Tour',
                    description: 'Section heading for tour description on the tour offer detail page',
                  })}
                </Heading>}
                {!!itineraryLocations.length && <Group direction="horizontal" verticalAlign="center" gap={4} wrap="wrap">
                  {!isCruiseFlashOffer && <BodyText variant="medium">
                    {intl.formatMessage({
                      id: 'KNlhvn46GvW4K6fBQqRR',
                      defaultMessage: 'Your tour takes you to:',
                      description: 'Label prefix for itinerary locations on the tour offer detail page',
                    })}
                  </BodyText>}
                  {itineraryLocations.map((location, locationIndex) => <Fragment key={locationIndex}>
                    {locationIndex !== 0 && <LineArrowRightIcon size="XS" />}
                    <BodyText variant="medium" weight="bold">{location}</BodyText>
                  </Fragment>)}
                </Group>}
                {description && <Description
                  fontSize="medium"
                  fontWeight="normal"
                  type="compact"
                  content={description}
                />}
              </VerticalSpacer>
              {campaignTier && <OverviewTourOffer2027TravelBanner
                eyebrow={tour2027LaunchCampaignConfig.campaignLabel}
                title={tour2027LaunchCampaignConfig.name}
                description={campaignTier.cta}
                termsLabel={intl.formatMessage({
                  id: 'nDcqGZQyn7u-WiksOkYR',
                  defaultMessage: 'T&Cs apply',
                  description: 'Label for the terms and conditions control in the 2027 tour campaign banner',
                })}
                onTermsClick={openTermsAndConditionsModal}
              />}
              {(isTourProductTypeStyled(offer.productType) || isLimitedTimeOffer) && <TourV2HighlightedInclusions
                variation={variation}
                onCheckAvailability={onCheckAvailability}
              />}
            </DescriptionArea>
          </ContentGridContainer>
          <div className="overview-area">
            <CSSBreakpoint min="tablet">
              <TourV2OverviewPanel
                offer={offer}
                activityLevel={activityLevel}
                purchasableOption={cheapestPurchasableOption}
                isOfferPurchasable={isOfferPurchasable}
                productType={offer.productType}
                variation={variation}
                onCheckAvailability={onCheckAvailability}
                guideType={offer.guideType}
              />
            </CSSBreakpoint>
            {(offer.hosts.length > 0 || offer.sponsors.length > 0) &&
              <TourV2HostsSponsors
                hosts={offer.hosts}
                sponsors={offer.sponsors}
                sponsorLabel={sponsorLabel}
              />}
          </div>
        </GridContainer>
        <SoftOfferPageWalledGarden>
          {isTourProductTypeStyled(offer.productType) && <Group direction="vertical" gap={24}>
            <Divider kind="primary" />
            <TourStyleKeyBenefitsSection
              offer={offer}
              onCheckAvailability={onCheckAvailability}
            />
          </Group>}
        </SoftOfferPageWalledGarden>
      </Group>
    </ProductPaletteProvider>
  )
}

function mapOfferPageStateToProps(offerPageState: OfferPageState): MappedProps {
  const boundaries = getSelectedVariationDepartureBoundaries(offerPageState)
  return {
    latestDeparture: boundaries?.latestDeparture,
    offer: offerPageState.tourV2Offer!,
    variation: offerPageState.tourV2Variation!,
    isOfferPurchasable: getIsTourOfferPurchasable(offerPageState),
  }
}

export default withOfferPageState(mapOfferPageStateToProps, TourV2Overview)
